import React, { useState } from 'react';
import Trigger from './Trigger';
import FlowBlock from './FlowBlock';
import AddStepButton from './AddStepButton';
import BlockTypeSelector from './BlockTypeSelector';
import './FlowBuilder.css';

const FlowBuilder = ({ automation, setAutomation }) => {
    const [showBlockSelector, setShowBlockSelector] = useState(false);
    const [selectedPosition, setSelectedPosition] = useState(null);

    const addStep = (type, position) => {
        // Don't allow adding trigger blocks through this function
        if (type === 'trigger') {
            return;
        }

        const newStep = {
            id: `step_${Date.now()}`,
            type: type,
            title: type === 'email' ? 'Send Email' : 'Delay',
            config: type === 'email' ? { 
                subject: '', 
                body: '', 
                to: '',
                from: ''
            } : { 
                duration: 60,
                unit: 'minutes'
            },
            status: 'pending'
        };

        const newSteps = [...automation.steps];
        newSteps.splice(position, 0, newStep);
        
        setAutomation({
            ...automation,
            steps: newSteps
        });
        
        setShowBlockSelector(false);
        setSelectedPosition(null);
    };

    const updateStep = (stepId, updates) => {
        const newSteps = automation.steps.map(step => 
            step.id === stepId ? { ...step, ...updates } : step
        );
        
        setAutomation({
            ...automation,
            steps: newSteps
        });
    };

    const updateTrigger = (updates) => {
        const trigger = automation.steps.find(step => step.type === 'trigger');
        if (trigger) {
            updateStep(trigger.id, updates);
        }
    };

    const deleteStep = (stepId) => {
        // Don't allow deleting the trigger block
        const stepToDelete = automation.steps.find(step => step.id === stepId);
        if (stepToDelete && stepToDelete.type === 'trigger') {
            return; // Don't delete trigger blocks
        }
        
        const newSteps = automation.steps.filter(step => step.id !== stepId);
        setAutomation({
            ...automation,
            steps: newSteps
        });
    };

    const handleAddStep = (position) => {
        setSelectedPosition(position);
        setShowBlockSelector(true);
    };

    return (
        <div className="flow-builder">
            <div className="flow-container">
                {automation.steps.length === 0 ? (
                    <div className="empty-flow">
                        <div className="empty-flow-content">
                            <div className="empty-flow-icon">⚡</div>
                            <h3>Start Building Your Automation</h3>
                            <p>Click the + button below to add your first step</p>
                            <AddStepButton 
                                onClick={() => handleAddStep(0)}
                                isFirst={true}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="flow-steps">
                        {(() => {
                            const trigger = automation.steps.find(step => step.type === 'trigger');
                            const actionSteps = automation.steps.filter(step => step.type !== 'trigger');
                            
                            return (
                                <>
                                    {trigger && (
                                        <div className="step-container">
                                            <Trigger 
                                                trigger={trigger}
                                                onUpdate={updateTrigger}
                                            />
                                            <AddStepButton 
                                                onClick={() => handleAddStep(1)}
                                                isFirst={false}
                                            />
                                        </div>
                                    )}
                                    {actionSteps.map((step, index) => (
                                        <div key={step.id} className="step-container">
                                            <FlowBlock 
                                                step={step}
                                                onUpdate={(updates) => updateStep(step.id, updates)}
                                                onDelete={() => deleteStep(step.id)}
                                            />
                                            <AddStepButton 
                                                onClick={() => handleAddStep(index + (trigger ? 2 : 1))}
                                                isFirst={false}
                                            />
                                        </div>
                                    ))}
                                </>
                            );
                        })()}
                    </div>
                )}
            </div>

            {showBlockSelector && (
                <BlockTypeSelector 
                    onSelect={(type) => addStep(type, selectedPosition)}
                    onClose={() => {
                        setShowBlockSelector(false);
                        setSelectedPosition(null);
                    }}
                />
            )}
        </div>
    );
};

export default FlowBuilder; 